import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import { getExportErrorMessage } from '../utils/exportErrorMessage';

export const REPORT_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const CANCELLED_MESSAGE = 'Report download cancelled.';
export const PREPARING_MESSAGE =
  'Preparing your report. Large reports can take about a minute. Keep this window open.';

type Options = {
  /** Closes the modal. Called for the close button, Escape and a click outside the modal. */
  onClose: () => void;
  /** The report covers one lounge, so a timeout message does not suggest picking fewer. */
  singleLounge?: boolean;
};

type Request = {
  endpoint: string;
  filename: string;
  body: unknown;
};

/**
 * What the two report download modals share: the in-flight request and its cancellation when
 * the modal unmounts, the loading and error state, and how closing behaves. The close button
 * and Escape cancel a running download and say so; a click outside the modal is ignored while
 * one runs, because that is the easy accident on a large modal.
 */
export function useReportDownload({ onClose, singleLounge = false }: Options) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cancelled on unmount, so the browser stops waiting on a file nobody will receive.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  // Cancels the request itself, not only through the unmount, so closing is complete even if a
  // parent ever keeps the modal mounted while hiding it.
  const close = useCallback(() => {
    if (loading) toast.info(CANCELLED_MESSAGE);
    abortRef.current?.abort();
    onClose();
  }, [loading, onClose]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (loading) return;
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [close, loading, onClose]);

  /** Runs the download. Resolves true once the file is saved; the caller then closes the modal. */
  const download = async ({
    endpoint,
    filename,
    body,
  }: Request): Promise<boolean> => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await ApiService.downloadFileBuffer(
        endpoint,
        REPORT_MIME,
        filename,
        body,
        {
          signal: controller.signal,
        },
      );
      toast.success(`Downloaded ${filename}`);
      return true;
    } catch (err: unknown) {
      // null: cancelled from the close button, which already said so
      const message = getExportErrorMessage(err, { singleLounge });
      if (message !== null) {
        console.error(err);
        setError(message);
      }
      return false;
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
    }
  };

  return { modalRef, loading, error, setError, close, download };
}
